import React, { useEffect, useState } from 'react';
import { Button, Card, Divider, Group, Radio, Stack, Stepper, Text, Title, Transition } from '@mantine/core';
import { ArrowRight, Check, Clock, Calendar, Dumbbell, Scale, User, Utensils, Star } from 'lucide-react';
import { generateArchetype } from '../utils/archetypeEngine';
import { protocolAxes } from '../data/protocolData';
export function ArchetypeWizard({
  onProtocolGenerated
}) {
  const [active, setActive] = useState(0);
  const [selections, setSelections] = useState({});
  const [transitioning, setTransitioning] = useState(false);
  const [contentVisible, setContentVisible] = useState(true);
  const handleSelection = (axisId, value) => {
    setSelections({
      ...selections,
      [axisId]: value
    });
  };
  const nextStep = () => {
    setTransitioning(true);
    setContentVisible(false);
  };
  useEffect(() => {
    if (transitioning && !contentVisible) {
      const timer = setTimeout(() => {
        if (active < protocolAxes.length - 1) {
          setActive(active + 1);
        } else {
          const protocol = generateArchetype(selections);
          onProtocolGenerated(protocol);
        }
        setContentVisible(true);
        setTransitioning(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [transitioning, contentVisible, active, selections, onProtocolGenerated]);
  const prevStep = () => {
    setTransitioning(true);
    setContentVisible(false);
  };
  const currentAxis = protocolAxes[active];
  const isLastStep = active === protocolAxes.length - 1;
  const canProceed = selections[currentAxis.id] !== undefined;
  const getStepIcon = stepIndex => {
    const icons = {
      0: <User size={18} data-id="element-18" />,
      1: <Clock size={18} data-id="element-19" />,
      2: <Utensils size={18} data-id="element-20" />,
      3: <Scale size={18} data-id="element-21" />,
      4: <Star size={18} data-id="element-22" />,
      5: <Calendar size={18} data-id="element-23" />,
      6: <Dumbbell size={18} data-id="element-24" /> // Exercise
    };
    return icons[stepIndex] || <Check size={18} data-id="element-25" />;
  };
  return <Card shadow="sm" p="xl" radius="md" className="bg-white" data-id="element-26">
      <Stepper active={active} onStepClick={setActive} className="mb-8" data-id="element-27">
        {protocolAxes.map((axis, index) => <Stepper.Step key={axis.id} label={axis.shortLabel} icon={getStepIcon(index)} data-id="element-28" />)}
      </Stepper>
      <Transition mounted={contentVisible} transition="fade" duration={300} timingFunction="ease" data-id="element-29">
        {styles => <div style={styles} className="mb-8" data-id="element-30">
            <Title order={3} className="mb-4 text-slate-800" data-id="element-31">
              {currentAxis.question}
            </Title>
            <Divider className="mb-4" variant="transparent" data-id="element-32" />
            <Text className="mb-8 text-slate-500 italic" data-id="element-33">
              {currentAxis.explanation}
            </Text>
            <Divider className="mb-8" variant="transparent" data-id="element-34" />
            <Stack spacing={0} data-id="element-35">
              <Radio.Group value={selections[currentAxis.id]} onChange={value => handleSelection(currentAxis.id, value)} data-id="element-36">
                {currentAxis.options.map(option => <Radio key={option.value} value={option.value} label={<Text size="md" className="text-slate-900" data-id="element-38">
                        {currentAxis.id === 'identity' ? option.label : option.description}
                      </Text>} className="mb-4" classNames={{
              body: 'flex items-center'
            }} data-id="element-37" />)}
              </Radio.Group>
            </Stack>
          </div>}
      </Transition>
      <Group justify="flex-end" mt="xl" data-id="element-39">
        <Button variant="light" onClick={prevStep} disabled={active === 0 || transitioning} data-id="element-40">
          Back
        </Button>
        <Button onClick={nextStep} disabled={!canProceed || transitioning} rightSection={isLastStep ? <Check size={16} data-id="element-42" /> : <ArrowRight size={16} data-id="element-43" />} className="bg-emerald-600 hover:bg-emerald-700" data-id="element-41">
          {transitioning && isLastStep ? 'Generating...' : isLastStep ? 'Generate Protocol' : 'Next Step'}
        </Button>
      </Group>
    </Card>;
}